import { Link } from 'react-router';
import {
  DashboardColumn,
  DashboardColumnSpinner,
} from '~/pages/dashboard/sections/DashboardColumn';

export interface ProductStockRow {
  id: string;
  productName: string;
  productLink?: string;
  label: React.ReactNode;
  metaItems: string[];
  action: React.ReactNode;
}

interface ProductStockColumnProps {
  title: string;
  subtitle: string;
  footerLink: string;
  footerLabel: string;
  rows: ProductStockRow[];
  isLoading: boolean;
  isError: boolean;
  emptyText: string;
  errorText: string;
}

export function ProductStockColumn({
  title,
  subtitle,
  footerLink,
  footerLabel,
  rows,
  isLoading,
  isError,
  emptyText,
  errorText,
}: ProductStockColumnProps) {
  let body: React.ReactNode;

  if (isLoading) {
    body = <DashboardColumnSpinner />;
  } else if (isError) {
    body = (
      <p className="text-sm font-medium text-neut-sec text-center px-6 py-8">
        {errorText}
      </p>
    );
  } else if (rows.length === 0) {
    body = (
      <p className="text-sm font-medium text-neut-sec px-6 py-8">{emptyText}</p>
    );
  } else {
    body = rows.map((row) => (
      <div
        key={row.id}
        className="flex flex-col gap-1 px-6 py-4 border-b border-neut-border last:border-b-0"
      >
        {/* Row 1: product name (link) + label */}
        <div className="flex items-center justify-between gap-2">
          {row.productLink ? (
            <Link
              to={row.productLink}
              className="text-sm font-medium text-prim hover:text-prim-hover min-w-0 truncate"
            >
              {row.productName}
            </Link>
          ) : (
            <span className="text-sm font-medium text-neut-prim min-w-0 truncate">
              {row.productName}
            </span>
          )}
          <div className="shrink-0">{row.label}</div>
        </div>
        {/* Row 2: meta items + action */}
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-neut-sec">
            {row.metaItems.join(' · ')}
          </p>
          <div className="shrink-0">{row.action}</div>
        </div>
      </div>
    ));
  }

  return (
    <DashboardColumn
      title={title}
      subtitle={subtitle}
      footerLink={footerLink}
      footerLabel={footerLabel}
    >
      {body}
    </DashboardColumn>
  );
}
